import { useState } from "react";
import {
  Ship, Home, PhoneCall, Wrench, Globe, ShoppingBag,
  Check, ArrowUpRight, Handshake,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const iconMap = { Ship, Home, PhoneCall, Wrench, Globe, ShoppingBag };

export default function Solutions() {
  const { t } = useLang();
  const items = t.solutions.items;
  const [active, setActive] = useState(0);
  const cur = items[active];
  const Icon = iconMap[cur.icon] || Globe;

  const scrollToContact = () =>
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="solutions"
      className="py-24 lg:py-32 bg-[#F9FAFB] relative overflow-hidden"
      data-testid="solutions-section"
    >
      <div className="absolute inset-0 bbm-grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="bbm-eyebrow mb-4">— {t.solutions.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B132B] leading-tight tracking-tight">
              {t.solutions.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#4B5563] text-lg leading-relaxed">{t.solutions.sub}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Tabs */}
          <div className="lg:col-span-4 flex flex-col gap-2" data-testid="solutions-tabs">
            {items.map((it, i) => {
              const I = iconMap[it.icon] || Globe;
              const isActive = i === active;
              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`solution-tab-${i}`}
                  className={`text-start p-4 rounded-lg border transition-all flex items-start gap-3 ${
                    isActive
                      ? "bg-[#0B132B] text-white border-[#0B132B] shadow-lg"
                      : "bg-white border-[#E5E7EB] text-[#0B132B] hover:border-[#0B132B]"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                      isActive ? "bg-[#FFB800] text-[#0B132B]" : "bg-[#F3F4F6] text-[#0B132B]"
                    }`}
                  >
                    <I className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`font-mono text-[10px] uppercase tracking-widest mb-1 truncate ${
                        isActive ? "text-[#FFB800]" : "text-[#4B5563]"
                      }`}
                    >
                      0{i + 1} · {it.tag}
                    </div>
                    <div className={`text-sm font-medium leading-tight ${isActive ? "text-white" : "text-[#0B132B]"}`}>
                      {it.title}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-8" data-testid="solution-detail">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 lg:p-10 h-full flex flex-col" key={active}>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-[#0047FF] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563]">
                      {cur.tag}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF] mt-0.5">
                      0{active + 1} / {items.length}
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="font-display text-2xl lg:text-3xl text-[#0B132B] leading-tight mb-4" data-testid="solution-title">
                {cur.title}
              </h3>
              <p className="text-[#4B5563] leading-relaxed mb-6">{cur.desc}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {cur.features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3"
                    data-testid={`solution-feature-${i}`}
                  >
                    <Check className="w-3.5 h-3.5 text-[#0047FF] mt-1 shrink-0" strokeWidth={3} />
                    <span className="text-sm text-[#0B132B] leading-snug">{f}</span>
                  </div>
                ))}
              </div>

              {cur.partner && (
                <div className="bg-[#0B132B] text-white rounded-md p-4 flex items-start gap-3 mb-6" data-testid="solution-partner">
                  <div className="w-8 h-8 rounded-md bg-[#FFB800] flex items-center justify-center shrink-0">
                    <Handshake className="w-4 h-4 text-[#0B132B]" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800] mb-0.5">
                      {cur.partner.label}
                    </div>
                    <div className="text-sm font-medium">{cur.partner.name}</div>
                  </div>
                </div>
              )}

              <button
                onClick={scrollToContact}
                data-testid="solution-cta"
                className="bbm-btn-primary mt-auto self-start"
              >
                {t.solutions.cta}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
